import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { supabase } from "../utils/auth";
import { logger } from "../utils/logger";
import { DEFAULT_LANG, LANG_SOURCE_MANUAL, isSupportedLang, persistLangPreference, resolveInitialLang } from "../utils/locale";

/*** Thunk: загрузка статей/новостей ***/
export const loadArticles = createAsyncThunk(
    'app/loadArticles',
    async ({ page = 1, limit = 10, published_only = true } = {}, { rejectWithValue }) => {
        try {
            // Используем прямой доступ к таблице через Supabase client (только необходимые поля)
            let query = supabase
                .from('articles')
                .select('id, title, excerpt, image_url, published_at, created_at, is_published');

            if (published_only) {
                query = query.eq('is_published', true);
            }

            // Сортировка: сначала по published_at (если есть), затем по created_at
            query = query
                .order('published_at', { ascending: false, nullsFirst: false })
                .order('created_at', { ascending: false })
                .range((page - 1) * limit, page * limit - 1);

            const { data, error } = await query;

            if (error) {
                throw error;
            }

            return data || [];
        } catch (error) {
            return rejectWithValue(error.message || 'Ошибка загрузки статей');
        }
    }
);

/*** Thunk: загрузка одной статьи по ID ***/
export const loadArticleById = createAsyncThunk(
    'app/loadArticleById',
    async (id, { rejectWithValue }) => {
        try {
            // Используем прямой доступ к таблице через Supabase client (только необходимые поля)
            const { data, error } = await supabase
                .from('articles')
                .select('id, title, content, image_url, published_at, created_at, is_published, excerpt')
                .eq('id', id)
                .single();

            if (error) {
                throw error;
            }

            return data;
        } catch (error) {
            return rejectWithValue(error.message || 'Ошибка загрузки статьи');
        }
    }
);

/*** Thunk: загрузка профиля пользователя ***/
export const loadUserProfile = createAsyncThunk(
    'app/loadUserProfile',
    async (userId, { rejectWithValue }) => {
        try {
            // maybeSingle: 0 rows — валидный empty (не PostgREST-ошибка), 1 row — профиль
            const { data, error } = await supabase
                .from('profiles')
                .select('id, email, first_name, last_name, user_in, phone, avatar_url, person_type, organization')
                .eq('id', userId)
                .maybeSingle();

            if (error) throw error;
            return data; // object | null
        } catch (error) {
            return rejectWithValue(error.message || 'Ошибка загрузки профиля');
        }
    }
);

/*** Thunk: загрузка ролей пользователя ***/
export const loadUserRoles = createAsyncThunk(
    'app/loadUserRoles',
    async (userId, { rejectWithValue, getState }) => {
        try {
            const { data, error } = await supabase
                .from('user_roles')
                .select('role')
                .eq('user_id', userId);

            if (error) throw error;
            const roles = data?.map(item => item.role) || [];
            return roles;
        } catch (error) {
            return rejectWithValue(error.message || 'Ошибка загрузки ролей');
        }
    },
    {
        condition: (userId, { getState }) => {
            const state = getState();
            // Предотвращаем выполнение, если уже идет загрузка или роли уже загружены
            if (state.app.loadingUserRoles) {
                return false; // Не выполняем запрос
            }
            // Если роли уже загружены, тоже не делаем повторный запрос
            if (state.app.userRoles && state.app.userRoles.length > 0) {
                return false;
            }
            return true; // Выполняем запрос
        }
    }
);

/*** Thunk: загрузка дронов пользователя ***/
export const loadUserDrones = createAsyncThunk(
    'app/loadUserDrones',
    async (userId, { rejectWithValue }) => {
        try {
            // Если userId не передан, получаем из auth
            let finalUserId = userId;
            if (!finalUserId) {
                const { data: { user }, error: userError } = await supabase.auth.getUser();
                if (userError || !user) {
                    return rejectWithValue('Пользователь не авторизован');
                }
                finalUserId = user.id;
            }

            // Загружаем дроны пользователя напрямую из таблицы (только необходимые поля)
            const { data: drones, error: dronesError } = await supabase
                .from('drones')
                .select('id, model, serial_number, registration_number, weight, drone_img, documents, system_type, drone_files, created_at')
                .eq('user_id', finalUserId)
                .order('created_at', { ascending: false });

            if (dronesError) {
                return rejectWithValue(dronesError.message || 'Ошибка загрузки дронов');
            }

            return { drones: drones || [] };
        } catch (error) {
            return rejectWithValue(error.message || 'Ошибка загрузки дронов');
        }
    }
);

/*** Thunk: загрузка полетов пользователя ***/
export const loadUserFlights = createAsyncThunk(
    'app/loadUserFlights',
    async ({ userId, date, status, sector, search } = {}, { rejectWithValue }) => {
        try {
            // Если userId не передан, получаем из auth
            let finalUserId = userId;
            if (!finalUserId) {
                const { data: { user }, error: userError } = await supabase.auth.getUser();
                if (userError || !user) {
                    return rejectWithValue('Пользователь не авторизован');
                }
                finalUserId = user.id;
            }

            // Строим запрос с join к связанным таблицам (только необходимые поля)
            let query = supabase
                .from('flights')
                .select(`
                    id,
                    user_id,
                    flight_date,
                    start_time,
                    end_time,
                    max_altitude,
                    purpose,
                    status,
                    notes,
                    confirmed_at,
                    route_length,
                    created_at,
                    updated_at,
                    user_geom,
                    drones:drone_id (
                        model,
                        registration_number
                    ),
                    flight_sectors (
                        sector_id,
                        sectors:sector_id (
                            id,
                            sector_id,
                            zone_type_id,
                            zone_types:zone_type_id (
                                id,
                                value,
                                name_ru,
                                name_kz,
                                name_en
                            )
                        )
                    )
                `)
                .eq('user_id', finalUserId);

            // Применяем фильтры
            if (date) {
                query = query.eq('flight_date', date);
            }
            if (status) {
                query = query.eq('status', status);
            }
            if (sector) {
                query = query.eq('sector_id', sector);
            }

            // Сортировка
            query = query.order('created_at', { ascending: false });

            const { data: flights, error: flightsError } = await query;

            if (flightsError) {
                return rejectWithValue(flightsError.message || 'Ошибка загрузки полетов');
            }

            // Применяем поиск на клиенте, если указан
            let filteredFlights = flights || [];
            if (search && search.trim()) {
                const searchLower = search.toLowerCase();
                filteredFlights = filteredFlights.filter((flight) => {
                    const droneModel = flight.drones?.model?.toLowerCase() || '';
                    // Получаем секторы из flight_sectors
                    const sectors = (flight.flight_sectors || [])
                        .map(fs => fs.sectors)
                        .filter(Boolean);
                    const sectorNames = sectors
                        .map(s => s.sector_id ? `микросектор ${s.sector_id}` : '')
                        .join(' ')
                        .toLowerCase();
                    const notes = flight.notes?.toLowerCase() || '';
                    return (
                        droneModel.includes(searchLower) ||
                        sectorNames.includes(searchLower) ||
                        notes.includes(searchLower)
                    );
                });
            }

            // Трансформируем данные для удобства использования
            const transformedFlights = filteredFlights.map((flight) => {
                // Получаем секторы из flight_sectors
                const sectors = (flight.flight_sectors || [])
                    .map(fs => fs.sectors)
                    .filter(Boolean);

                return {
                    ...flight,
                    drone_name: flight.drones?.model,
                    sectors: sectors,
                    sector_name: sectors.length > 0
                        ? sectors.map(s => s.sector_id ? `Микросектор ${s.sector_id}` : '').join(', ')
                        : null,
                };
            });

            return { flights: transformedFlights };
        } catch (error) {
            return rejectWithValue(error.message || 'Ошибка загрузки полетов');
        }
    }
);

// Thunk для удаления заявки на полет
export const deleteFlight = createAsyncThunk(
    'app/deleteFlight',
    async (flightId, { rejectWithValue }) => {
        try {
            // Получаем текущего пользователя
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                return rejectWithValue('Пользователь не авторизован');
            }

            // Проверяем, что заявка принадлежит пользователю
            const { data: flight, error: flightError } = await supabase
                .from('flights')
                .select('id, user_id')
                .eq('id', flightId)
                .single();

            if (flightError || !flight) {
                return rejectWithValue('Заявка не найдена');
            }

            if (flight.user_id !== user.id) {
                return rejectWithValue('Нет прав на удаление этой заявки');
            }

            // Удаляем связанные записи (CASCADE должно работать автоматически, но на всякий случай)
            // Удаляем из flight_sectors
            await supabase
                .from('flight_sectors')
                .delete()
                .eq('flight_id', flightId);

            // Удаляем из flight_drones
            await supabase
                .from('flight_drones')
                .delete()
                .eq('flight_id', flightId);

            // Удаляем саму заявку
            const { error: deleteError } = await supabase
                .from('flights')
                .delete()
                .eq('id', flightId);

            if (deleteError) {
                return rejectWithValue(deleteError.message || 'Ошибка удаления заявки');
            }

            return flightId;
        } catch (error) {
            return rejectWithValue(error.message || 'Ошибка удаления заявки');
        }
    }
);



const getInitialLang = () => {
    try {
        return resolveInitialLang();
    } catch (error) {
        logger.error('Ошибка определения языка:', error);
        return DEFAULT_LANG;
    }
};

const initialState = {
    serviceName: {
        nameRu: "Управление дронами",
        nameKz: "Дрондарды басқару",
        nameEn: "Drone management",
    },
    lang: getInitialLang(),
    loading: { passport: false, articles: false, article: false, drones: false, flights: false, profile: false, roles: false },
    loadingUserRoles: false,
    articles: [],
    articlesPagination: null,
    articlesError: null,
    currentArticle: null,
    currentArticleError: null,
    userDrones: [],
    userDronesError: null,
    userFlights: [],
    userFlightsError: null,
    userProfile: null,
    userProfileError: null,
    userRoles: [],
    userRolesError: null,

};

const appSlice = createSlice({
    name: "app",
    initialState,
    reducers: {
        setLang: (state, { payload }) => {
            if (!isSupportedLang(payload)) return;
            state.lang = payload;
            try {
                persistLangPreference(payload, LANG_SOURCE_MANUAL);
            } catch (error) {
                logger.error('Ошибка сохранения языка в localStorage:', error);
            }
        },
        clearCurrentArticle: (state) => {
            state.currentArticle = null;
            state.currentArticleError = null;
            state.loading.article = false;
        },
        clearUserRoles: (state) => {
            state.userRoles = [];
            state.userRolesError = null;
            state.loadingUserRoles = false;
            state.loading.roles = false;
        },

    },
    extraReducers: (builder) => {
        builder

            .addCase(loadArticles.pending, (state) => {
                state.loading.articles = true;
                state.articlesError = null;
            })
            .addCase(loadArticles.fulfilled, (state, { payload }) => {
                state.loading.articles = false;
                // payload теперь массив напрямую, а не объект с articles
                state.articles = Array.isArray(payload) ? payload : (payload?.articles || []);
                state.articlesPagination = payload?.pagination || null;
            })
            .addCase(loadArticles.rejected, (state, { payload }) => {
                state.loading.articles = false;
                state.articlesError = payload || 'Ошибка загрузки статей';
                state.articles = [];
            })

            .addCase(loadArticleById.pending, (state) => {
                state.loading.article = true;
                state.currentArticleError = null;
            })
            .addCase(loadArticleById.fulfilled, (state, { payload }) => {
                state.loading.article = false;
                state.currentArticle = payload;
            })
            .addCase(loadArticleById.rejected, (state, { payload }) => {
                state.loading.article = false;
                state.currentArticleError = payload || 'Ошибка загрузки статьи';
                state.currentArticle = null;
            })

            .addCase(loadUserProfile.pending, (state) => {
                state.loading.profile = true;
                state.userProfileError = null;
            })
            .addCase(loadUserProfile.fulfilled, (state, { payload }) => {
                state.loading.profile = false;
                state.userProfile = payload;
            })
            .addCase(loadUserProfile.rejected, (state, { payload }) => {
                state.loading.profile = false;
                state.userProfileError = payload;
                state.userProfile = null;
            })
            .addCase(loadUserRoles.pending, (state) => {
                state.loadingUserRoles = true;
                state.loading.roles = true;
                state.userRolesError = null;
            })
            .addCase(loadUserRoles.fulfilled, (state, { payload }) => {
                state.loadingUserRoles = false;
                state.loading.roles = false;
                state.userRoles = payload || [];
            })
            .addCase(loadUserRoles.rejected, (state, { payload }) => {
                state.loadingUserRoles = false;
                state.loading.roles = false;
                state.userRolesError = payload;
                state.userRoles = [];
            })
            .addCase(loadUserDrones.pending, (state) => {
                state.loading.drones = true;
                state.userDronesError = null;
            })
            .addCase(loadUserDrones.fulfilled, (state, { payload }) => {
                state.loading.drones = false;
                const drones = payload?.drones || payload || [];
                state.userDrones = Array.isArray(drones) ? drones : [];
            })
            .addCase(loadUserDrones.rejected, (state, { payload }) => {
                state.loading.drones = false;
                state.userDronesError = payload || 'Ошибка загрузки дронов';
                state.userDrones = [];
            })

            .addCase(loadUserFlights.pending, (state) => {
                state.loading.flights = true;
                state.userFlightsError = null;
            })
            .addCase(loadUserFlights.fulfilled, (state, { payload }) => {
                state.loading.flights = false;
                const flights = payload?.flights || payload || [];
                state.userFlights = Array.isArray(flights) ? flights : [];
            })
            .addCase(loadUserFlights.rejected, (state, { payload }) => {
                state.loading.flights = false;
                state.userFlightsError = payload || 'Ошибка загрузки полетов';
                state.userFlights = [];
            })
            .addCase(deleteFlight.pending, (state) => {
                state.loading.flights = true;
            })
            .addCase(deleteFlight.fulfilled, (state, { payload }) => {
                state.loading.flights = false;
                // Удаляем заявку из списка
                state.userFlights = state.userFlights.filter(f => f.id !== payload);
            })
            .addCase(deleteFlight.rejected, (state, { payload }) => {
                state.loading.flights = false;
                state.userFlightsError = payload || 'Ошибка удаления заявки';
            })

    },
});

export const {setLang, clearCurrentArticle, clearUserRoles} = appSlice.actions;

export default appSlice.reducer;
